import { FunctionComponent, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DrawerActions } from '@/components/Drawer/DrawerActions';
import { LoadingButton } from '@mui/lab';
import { useNotification } from '@starjack/features/notifications';
import { CreateWebshop, isOfTypeWebshop, Webshop } from '@/models/webshop';
import { MultiLangInput } from '@/components/Layout/MultiLangInput/MultiLangInput';
import { ColorInput } from '@/components/Layout/ColorInput';
import {
    usePostWebshopMutation,
    usePutWebshopMutation,
} from '@/api/v2/configurations';
import set from 'lodash/set';
import { LogoUploadContainer } from '@/pages/standardShops/components/LogoUploadContainer';
import { MarkdownInput } from '@starjack/shared-components';
import { useAuthentication } from '@/hooks/useAuthentication';
import {
    useGetLiftOperatorQuery,
    usePutLiftOperatorMutation,
} from '@starjack/entities/liftoperator';

type WebshopDrawerContentProps = {
    webshop: Webshop | CreateWebshop;
    onClose: () => void;
};

export const WebshopDrawerContent: FunctionComponent<
    WebshopDrawerContentProps
> = ({ webshop, onClose }) => {
    const [localWebshop, setLocalWebshop] = useState<Webshop | CreateWebshop>(
        webshop
    );
    const { addNotification } = useNotification();
    const [putWebshop, { isLoading: putWebshopLoading }] =
        usePutWebshopMutation();
    const [postWebshop, { isLoading: postWebshopLoading }] =
        usePostWebshopMutation();
    const { liftOperatorData } = useAuthentication();
    const [putLiftOperator, { isLoading: isPutLiftOperatorLoading }] =
        usePutLiftOperatorMutation();

    const { data: liftOperator } = useGetLiftOperatorQuery({
        liftOperatorId: liftOperatorData.liftOperatorId,
    });

    const [localInfoText, setLocalInfoText] = useState<{
        de: string;
        en: string;
    }>({ de: '', en: '' });

    const isConfigurationEnabled = liftOperator?.enableConfiguration ?? false;

    useEffect(() => {
        setLocalWebshop(webshop);
    }, [webshop]);

    useEffect(() => {
        if (liftOperator?.infoText) {
            setLocalInfoText(liftOperator.infoText);
        }
    }, [liftOperator]);

    const setField = (key: string, value: string) => {
        setLocalWebshop((prev) => {
            const copy = { ...prev };
            set(copy, key, value);
            return copy;
        });
    };

    const onChangeHandler = (key: string) => (e) => {
        setField(key, e.target.value);
    };

    const saveLiftOperatorInfoText = async () => {
        if (!liftOperator) return;

        await putLiftOperator({
            ...liftOperator,
            infoText: localInfoText,
        }).unwrap();
    };

    const onCreateOrUpdate = () => {
        const isNewWebshop = !isOfTypeWebshop(localWebshop);
        const mutationHandler = isNewWebshop ? postWebshop : putWebshop;

        const promises: Promise<unknown>[] = [
            mutationHandler(localWebshop).unwrap(),
        ];

        if (!isNewWebshop && liftOperator) {
            promises.push(saveLiftOperatorInfoText());
        }

        Promise.all(promises)
            .then(([webshopResult]) => {
                addNotification({
                    message: 'Speichern erfolgreich',
                    type: 'success',
                });

                setLocalWebshop(webshopResult as Webshop);

                if (!isNewWebshop) {
                    onClose();
                }
            })
            .catch((reason) => {
                console.error(reason);

                let message =
                    'Fehler beim Speichern. Bitte später noch einmal versuchen';
                if (
                    reason?.data?.errors[0]?.error === 'username_already_exists'
                ) {
                    message =
                        'Ein User mit diesem Benutzernamen existiert bereits. Bitte einen anderen wählen';
                }

                addNotification({
                    message,
                    type: 'error',
                });
            });
    };

    return (
        <Box
            sx={{
                width: '880px',
            }}
        >
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.dark,
                    color: '#fff',
                    p: 4,
                })}
            >
                <Typography variant='h6'>
                    {isOfTypeWebshop(localWebshop)
                        ? 'Webshop bearbeiten'
                        : 'Neuen Webshop erstellen'}
                </Typography>
            </Box>

            <Stack
                spacing={6}
                sx={{
                    p: 4,
                }}
            >
                {!isOfTypeWebshop(localWebshop) ? (
                    <TextField
                        label='Name:'
                        value={localWebshop.vanityIdentifier}
                        onChange={onChangeHandler('vanityIdentifier')}
                        fullWidth
                    />
                ) : (
                    <>
                        <Stack spacing={2}>
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                }}
                                variant='body2'
                            >
                                Hier lässt sich der individuelle Teil der
                                Shop-URL anpassen (z.B.: name.shop.starjack.at)
                            </Typography>
                            <TextField
                                label='Name:'
                                value={localWebshop.vanityIdentifier}
                                onChange={onChangeHandler('vanityIdentifier')}
                                fullWidth
                            />
                        </Stack>

                        <MultiLangInput
                            label='Impressum-URL:'
                            value={localWebshop.imprintUrl}
                            onChange={(langString) => {
                                setLocalWebshop((prevWebshop) => {
                                    const copyWebshop = {
                                        ...prevWebshop,
                                    };
                                    copyWebshop.imprintUrl = langString;

                                    return copyWebshop;
                                });
                            }}
                            fullWidth
                        />

                        <MultiLangInput
                            label='AGB-URL: '
                            value={localWebshop.termsAndConditionsUrl}
                            onChange={(langString) => {
                                setLocalWebshop((prevWebshop) => {
                                    const copyWebshop = {
                                        ...prevWebshop,
                                    };
                                    copyWebshop.termsAndConditionsUrl =
                                        langString;

                                    return copyWebshop;
                                });
                            }}
                            fullWidth
                        />

                        <MultiLangInput
                            label='Datenschutz-URL: '
                            value={localWebshop.dataPrivacyUrl}
                            onChange={(langString) => {
                                setLocalWebshop((prevWebshop) => {
                                    const copyWebshop = {
                                        ...prevWebshop,
                                    };
                                    copyWebshop.dataPrivacyUrl = langString;

                                    return copyWebshop;
                                });
                            }}
                            fullWidth
                        />

                        <ColorInput
                            label='Primärfarbe:'
                            onChange={onChangeHandler('primaryColor')}
                            value={localWebshop.primaryColor}
                        />

                        <ColorInput
                            label='Primäre Farbe Hell:'
                            onChange={onChangeHandler('primaryLightColor')}
                            value={localWebshop.primaryLightColor}
                        />

                        <MultiLangInput
                            label='Produktkategorie Tages- und Mehrtagestickets umbenennen:'
                            value={localWebshop.dayTicketsButtonText}
                            onChange={(langString) => {
                                setLocalWebshop((prevWebshop) => {
                                    const copyWebshop = {
                                        ...prevWebshop,
                                    };
                                    copyWebshop.dayTicketsButtonText =
                                        langString;

                                    return copyWebshop;
                                });
                            }}
                            fullWidth
                        />

                        <MultiLangInput
                            label='Produktkategorie Saison- und Jahrestickets umbenennen'
                            value={localWebshop.seasonTicketsButtonText}
                            onChange={(langString) => {
                                setLocalWebshop((prevWebshop) => {
                                    const copyWebshop = {
                                        ...prevWebshop,
                                    };
                                    copyWebshop.seasonTicketsButtonText =
                                        langString;

                                    return copyWebshop;
                                });
                            }}
                            fullWidth
                        />

                        {!isConfigurationEnabled && liftOperator && (
                            <Alert severity='warning'>
                                Die Konfiguration ist derzeit deaktiviert. Bitte
                                den Administrator kontaktieren, um die
                                Konfiguration zu aktivieren.
                            </Alert>
                        )}

                        <Box
                            sx={{
                                pointerEvents: !isConfigurationEnabled
                                    ? 'none'
                                    : 'auto',
                                opacity: !isConfigurationEnabled ? 0.5 : 1,
                            }}
                        >
                            <Stack spacing={6}>
                                <MarkdownInput
                                    label='Infotext Deutsch'
                                    value={localInfoText?.de ?? ''}
                                    onChange={(value) =>
                                        setLocalInfoText((prev) => ({
                                            ...prev,
                                            de: value,
                                        }))
                                    }
                                />

                                <MarkdownInput
                                    label='Infotext Englisch'
                                    value={localInfoText?.en ?? ''}
                                    onChange={(value) =>
                                        setLocalInfoText((prev) => ({
                                            ...prev,
                                            en: value,
                                        }))
                                    }
                                />
                            </Stack>
                        </Box>

                        <TextField
                            label='Tag Manager Code'
                            value={localWebshop.tagManagerCode}
                            onChange={onChangeHandler('tagManagerCode')}
                            fullWidth
                        />

                        <LogoUploadContainer
                            label='Logo'
                            subtitle={
                                <>
                                    <Typography variant='body2'>
                                        1.) Mögliche Datei-Formate: .jpg, .png
                                    </Typography>
                                    <Typography variant='body2'>
                                        2.) Die Größe des Logos passt starjack
                                        automatisch an
                                    </Typography>
                                </>
                            }
                            type='logo'
                            imageUrl={localWebshop.logoUrl}
                            webshop={localWebshop}
                        />

                        <LogoUploadContainer
                            label='Header image'
                            subtitle={
                                <>
                                    <Typography variant='body2'>
                                        1.) Mögliche Datei-Formate: .jpg, .png
                                    </Typography>
                                    <Typography variant='body2'>
                                        2.) Größe: Höhe 600 Pixel, das Bild wird
                                        automatisch mittig ausgerichtet
                                    </Typography>
                                </>
                            }
                            type='headerImage'
                            imageUrl={localWebshop.headerImageUrl}
                            webshop={localWebshop}
                        />

                        {/*<Field>*/}
                        {/*    <ColorInput*/}
                        {/*        label='Button Text Color:'*/}
                        {/*        onChange={onChangeHandler(*/}
                        {/*            'buttonTextColor'*/}
                        {/*        )}*/}
                        {/*        value={localWebshop.buttonTextColor}*/}
                        {/*    />*/}
                        {/*</Field>*/}

                        {/*<Field>*/}
                        {/*    <ColorInput*/}
                        {/*        label='Button Background Color:'*/}
                        {/*        onChange={onChangeHandler(*/}
                        {/*            'buttonBackgroundColor'*/}
                        {/*        )}*/}
                        {/*        value={*/}
                        {/*            localWebshop.buttonBackgroundColor*/}
                        {/*        }*/}
                        {/*    />*/}
                        {/*</Field>*/}

                        {/*<Field>*/}
                        {/*    <ColorInput*/}
                        {/*        label='Filter Text Color:'*/}
                        {/*        onChange={onChangeHandler(*/}
                        {/*            'filterTextColor'*/}
                        {/*        )}*/}
                        {/*        value={localWebshop.filterTextColor}*/}
                        {/*    />*/}
                        {/*</Field>*/}

                        {/*<Field>*/}
                        {/*    <ColorInput*/}
                        {/*        label='Filter Background Color:'*/}
                        {/*        onChange={onChangeHandler(*/}
                        {/*            'filterBackgroundColor'*/}
                        {/*        )}*/}
                        {/*        value={*/}
                        {/*            localWebshop.filterBackgroundColor*/}
                        {/*        }*/}
                        {/*    />*/}
                        {/*</Field>*/}
                    </>
                )}

                <DrawerActions>
                    <Stack
                        sx={{
                            alignItems: 'center',
                        }}
                        spacing={2}
                        direction='row'
                    >
                        <Button variant='outlined' onClick={onClose}>
                            Abbrechen
                        </Button>

                        <LoadingButton
                            variant='contained'
                            onClick={onCreateOrUpdate}
                            loading={
                                putWebshopLoading ||
                                postWebshopLoading ||
                                isPutLiftOperatorLoading
                            }
                        >
                            {isOfTypeWebshop(localWebshop)
                                ? `Speichern`
                                : `Anlegen und fortfahren`}
                        </LoadingButton>
                    </Stack>
                </DrawerActions>
            </Stack>
        </Box>
    );
};
